import React from 'react';

// Employee Role enum
export enum EmployeeRole {
  Admin = 'Admin',
  LineSupervisor = 'LineSupervisor',
  MachineOperator = 'MachineOperator',
  CleaningWorker = 'CleaningWorker',
  NichromeOperators = 'NichromeOperators',
  PackagingWorker = 'PackagingWorker'
}

// Employee Data types
export interface EmployeeData {
  empId: string;
  empName: string;
  posistionId: string;
  posistionName: string;
  branch: string;
  department: string;
  role: string;
}

// User related types
export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLogin: string;
  permissions: string[];
}

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
}

export interface CreateUserRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  password: string;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
}

// API Response types
export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message: string;
  success: boolean;
}

// Error types
export interface ApiError {
  code: string;
  message: string;
  details?: any;
  timestamp: string;
}

// Authentication types
export interface LoginRequest {
  username: string;
  password: string;
  selectedRole: EmployeeRole;
}

export interface LoginResponse {
  success: boolean;
  data: {
    username: string;
    employeeData: EmployeeData;
    token: string;
  } | null;
  message: string;
}

export interface AuthState {
  user: EmployeeData | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  selectedRole: EmployeeRole | null;
}

// BFF types
export interface BffResponse<T> {
  data: T;
  metadata: {
    source: string;
    timestamp: string;
    version: string;
  };
  status: 'success' | 'error';
}

// Environment types
export interface EnvironmentConfig {
  apiBaseUrl: string;
  timeout: number;
  retryAttempts: number;
}

// Form types
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'select' | 'textarea' | 'checkbox';
  required: boolean;
  validation?: any;
  options?: { value: string; label: string }[];
}

// Component props types
export interface BaseComponentProps {
  className?: string;
  children?: React.ReactNode;
}

export interface LoadingProps extends BaseComponentProps {
  size?: 'small' | 'medium' | 'large';
  text?: string;
}

export interface ButtonProps extends BaseComponentProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
}

// Table types
export interface TableColumn<T> {
  key: keyof T;
  header: string;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  onRowClick?: (row: T) => void;
  onSort?: (key: keyof T, direction: 'asc' | 'desc') => void;
  sortKey?: keyof T;
  sortDirection?: 'asc' | 'desc';
  className?: string;
}

// Batch types
export interface AllBatchDto {
  batchkey: string;
  batchCode: string;
  assignedManagerId: string;
  plantId: string;
  noOfLots: number;
  loss: number;
  created_at: string;
  status: string;
}

export interface BatchPagination {
  batches: AllBatchDto[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

export interface BatchFilters {
  pageNo?: number;
  pageSize?: number;
  plant?: string;
  status?: string;
  batch?: string;
}

// Create Batch types
export interface EmployeeByRoleResponse {
  success: boolean;
  data: EmployeeData[];
  message: string;
}

export interface ProductionLot {
  prodnLotNo: string;
  year: string;
  season: string;
  growerCode: string;
  organizerCode: string;
  prodnCode: string;
  area: string;
  noOfBags: number | null;
  enteredBy: string;
  createdTime: string;
  modifiedBy: string;
  modifiedTime: string | null;
  procuredQty: number | null;
  lotType: string;
}

export interface ProductionLotsResponse {
  success: boolean;
  data: ProductionLot[];
  message: string;
}

export interface CropDetails {
  cropCode: number;
  name: string;
  cropType: string;
  seedMoisture: string;
  createdBy: string;
  createdDateTime: string;
  lastUpdatedBy: string;
  lastUpdatedDateTime: string;
}

export interface CropResponse {
  success: boolean;
  data: CropDetails;
  message: string;
}

export interface ProductByCrop {
  productId: string;
  cropCode: string;
  marketCode: string;
  productName: string;
  germinationRefValue: number | null;
  gotRefValue: number | null;
  odvRefValue: number | null;
  isSalesReturnAllowed: string;
  trimurthiProductCol: string | null;
  createdBy: string;
  createdDateTime: string;
  lastUpdatedBy: string;
  lastUpdatedDateTime: string;
  sowingSeed: number | null;
  sowingAfterInDays: number | null;
  harvestInDays: number | null;
  detasselingPass1AfterInDays: number | null;
  detasselingPass2AfterInDays: number | null;
  detasselingPass3AfterInDays: number | null;
  productType: string;
  moq: number | null;
}

export interface ProductsByCropResponse {
  success: boolean;
  data: ProductByCrop[];
  message: string;
}

export interface PackSize {
  productId: string;
  weight: string;
  effectiveDate: string;
  teamId: number;
  effectiveTill: string;
  description: string;
  currentBillingPrice: number;
  currentMRP: number;
  enteredBy: string;
  enteredTime: string;
  updatedBy: string;
  updatedTime: string;
  discount: number;
  unit: string;
}

export interface PackSizesResponse {
  success: boolean;
  data: PackSize[];
  message: string;
}

export interface PackagingMaterial {
  createdAt: string;
  packagingMaterialkey: string;
  materialType: string;
  materialName: string;
  materialWeight: number;
  description: string;
  key: string;
}

export interface PackagingMaterialsResponse {
  success: boolean;
  data: PackagingMaterial[];
  message: string;
}
export interface ChemicalAdd {
  materialType: string
  materialName: string
  materialWeight: number
  description: string
}

export interface chemicalUpdate {
  materialType: string
  materialName: string
  materialWeight: number
  description: string
  key: string
}

export interface ChemicalIngredient {
  id: number;
  type: string;
  name: string;
  units: string;
  comment: string;
}

export interface ChemicalIngredientsResponse {
  success: boolean;
  data: ChemicalIngredient[];
  message: string;
}

// Form Data Types
export interface LotDetails {
  lotNumber: string;
  productionQuantity: number;
  crop: any;
  year: string
}


export interface ProductDetails {
  productId: string;
  hybridId?: string;
  productName: string;
  expectedQuantity: number;
  lossPercentage: number;
  finalProductLOTNumber: string;
  packagingSets: PackagingSet[];
  packMaterials: PackMaterial[];
}

export interface PackagingSet {
  primaryPackSize: string;
  primaryBagsCount: number;
  secondaryBagsCount: number;
  unit?: string;
}

export interface PackMaterial {
  materialType: string;
  materialName: string;
  totalWeight: number;
  numberOfRolls: number;
  // key: string;
}

export interface CreateBatchRequest {
  batchCode: string;
  assignedManagerId: string;
  plantId: string;
  batchLots: {
    cropId: number;
    lotNumber: string;
    lotWeight: number;
  }[];
  batchProducts: {
    finl_lot_id: string;
    productName: string;
    hybriId: string
    expectedOutputWeight: number;
    benchmarkLossPercentage: number;
    batchProductPacks: {
      packSize: string;
      primaryBagsCount: number;
      secondaryBagsCount: number;
    }[];
    productPackmaterials: {
      materialName: string;
      materialType: string;
      unit: string;
      noOfrolls: number;
      weight: number;
    }[];
  }[];
}

export interface CreateBatchResponse {
  success: boolean;
  data: {
    batchCode: string;
    batchkey: string;
    assignedManagerId: string;
  };
  message: string;
}

// export interface BatchDetailsResponse {
//   success: boolean;
//   data: {
//     batchkey: string;
//     batchName: string;
//     assignedManagerId: string;
//     plantId: string;
//     status: string;
//     createdBy: string | null;
//     batchLots: BatchLotDetail[];
//     batchProducts: BatchProductDetail[];
//   };
//   message: string;
// }

// export interface BatchLotDetail {
//   lotkey: string;
//   cropid: number;
//   lotNumber: string;
//   lotWeight: number;
// }

export interface BatchDetailsResponse {
  success: boolean;
  data: {
    batchkey: string;
    batchName: string;
    assignedManagerId: {
      userid: string;
      username: string;
    } | null;
    plantId: string;
    status: string;
    createdBy: {
      userid: string;
      username: string;
    } | null;
    createdDate: string;
    batchLots: BatchLotDetail[];
    batchProducts: BatchProductDetail[];
    processingLoss: ProcessingLoss
  };
  message: string;
}
export interface ProcessingLoss {
  processedbagCount: number
  secondarybagsCount: number
  packedQunatity: number
  processingLoss: number
  packingLoss: number
  totalLoss: number
  wastageQuantity: number
}


// Update your BatchLotDetail interface
export interface BatchLotDetail {
  lotkey: string;
  cropid: number;
  lotNumber: string;
  lotWeight: number;
}

// Update your BatchProductDetail interface  
// interface BatchProductDetail {
//   productkey: string;
//   finl_lot_id: string;
//   productName: string;
//   expectedOutputWeight: {
//     source: string;
//     parsedValue: number;
//   };
//   benchmarkLossPercentage: {
//     source: string;
//     parsedValue: number;
//   };
//   batchProductPacks: Array<{
//     packSizeKey: string;
//     packSize: string;s
//     primaryBagsCount: number;
//     secondaryBagsCount: number;
//   }>;
//   productchemicals: Array<{
//     chemicalkey: string;
//     chemicalName: string;
//     weight: {
//       source: string;
//       parsedValue: number;
//     };
//     waterPercentage: {
//       source: string;
//       parsedValue: number;
//     };
//     colorName: string;
//   }>;
//   productPackmaterials: Array<{
//     packagingMaterialkey: string;
//     materialName: string;
//     materialType: string;
//     unit: string;
//     noOfrolls: number;
//   }>;
//   productProcesses: Array<{
//     productProcesskey: string;
//     processType: string;
//     processSequence: number;
//     createdDate: string;
//     tasks: any[];
//   }>;
// }


export interface BatchProductDetail {
  productkey: string
  finl_lot_id: string
  productName: string
  hybridId?: string
  expectedOutputWeight: number
  benchmarkLossPercentage: number
  batchProductPacks: BatchProductPack[]
  productchemicals: Productchemical[]
  productPackmaterials: ProductPackmaterial[]
  productProcesses: ProductProcess[]
}

export interface BatchProductPack {
  packSizeKey: string
  packSize: string
  primaryBagsCount: number
  secondaryBagsCount: number
}

export interface Productchemical {
  chemicalKey?: string
  cropId: number
  iType: string
  iUnits: string
  chemicalName: string
  dosageQty: string
  productCode: any
}

export interface ProductPackmaterial {
  packagingMaterialkey: string
  materialName: string
  materialType: string
  weight: number
  usedWeight: any
  unit: string
  noOfrolls: number
}

export interface ProductProcess {
  productProcesskey: string
  processType: string
  processSequence: number
  createdDate: string
  status: string
  startDate?: string
  endDate?: string
  tasks: Task[]
}

export interface Task {
  taskKey: string
  productProcessKey: string
  status: string
  taskType: string
  startDate: string
  endDate: string
  assignedUser: AssignedUser
  taskslogs: Taskslog[]
}

export interface AssignedUser {
  userid: string
  username: string
}

export interface Taskslog {
  logkey: string
  logDate: string
  wastageType?: string
  chemicalname?: string
  wastageQty?: number
  cleanedBaggagesCount?: number
  cleanedBaggagesWeight?: number
  chemicalUsedQty?: number
  waterPercentage?: number
  secondaryBaggageWeight: number
  notes: string
  createdAt: string
  createdBy: CreatedBy2
}

export interface CreatedBy2 {
  userid: string
  username: string
}


export interface BatchProductPack {
  packSizeKey: string;
  packSize: string;
  primaryBagsCount: number;
  secondaryBagsCount: number;
}

export interface BatchProductChemical {
  chemicalkey: string;
  chemicalName: string;
  weight: number;
  waterPercentage: number;
  colorName: string;
}

export interface BatchProductPackMaterial {
  packagingMaterialkey: string;
  materialName: string;
  unit: string;
  noOfrolls: number;
}

export interface BatchProductProcess {
  productProcesskey: string;
  processType: string;
  processSequence: number;
  createdDate: string;
}

// export interface BatchProductDetail {
//   productkey: string;
//   finl_lot_id: string;
//   productName: string;
//   expectedOutputWeight: number;
//   benchmarkLossPercentage: number;
//   batchProductPacks: BatchProductPack[];
//   productchemicals: BatchProductChemical[];
//   productPackmaterials: BatchProductPackMaterial[];
//   productProcesses: BatchProductProcess[];
// } 

export enum ProcessStatus {
  Created = 'Created',
  In_Progress = 'In_Progress',
  Completed = 'Completed'
}

export interface GetTaskRequest {
  productName?: string;
  batchName?: string;
  taskType?: string;
  status?: ProcessStatus;
  startdate?: string;
  dateTime?: string;
  pageNo: number;
  pageSize: number;
}

export interface TaskdetailDto {
  taskKey: string;
  taskProcessName: string;
  productName: string;
  assignDate : Date;
  status: string;
  startDate: Date;
  endDate: Date;
  lastUpdatedDate?: Date;
}

export interface TaskPaginationModel {
  tasks: TaskdetailDto[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}

export interface TaskPaginationResponse {
  success: boolean;
  data: TaskPaginationModel;
  message: string;
}

export interface AssignedBatch {
  batchkey: string;
  batchCode: string;
  assignedManagerId: string;
  plantId: string;
  noOfLots: number;
  loss: number;
  created_at: string; // Consider using Date type if you parse it upon reception
  status: string;
}

export interface AssignedBatchPagination {
  batches: AssignedBatch[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
}



// ----- for the processing batches ------------


// interface AssignedUser {
//   userid: string;
//   username: string;
// }

export interface BatchLot {
  lotkey: string;
  cropid: number;
  lotNumber: string;
  lotWeight: number;
}
export interface ProductChemical {
  cropId: number
  iType: string
  iUnits: string
  chemicalName: string
  dosageQty: string
  productCode: any
}

export interface ProductPackMaterial {
  packagingMaterialkey: string
  materialName: string
  materialType: string
  weight: number
  usedWeight?: number
  unit: string
  noOfrolls: number
}

export interface BatchProduct {
  productkey: string;
  finl_lot_id: string;
  productName: string;
  expectedOutputWeight: number;
  benchmarkLossPercentage: number;
  batchProductPacks: BatchProductPack[];
  productchemicals: ProductChemical[];
  productPackmaterials: ProductPackMaterial[];
  productProcesses: ProductProcess[]
  // productProcesses is in the response but not used in the UI, so it's optional here
}

export interface BatchDetails {
  batchkey: string
  batchName: string
  assignedManagerId: AssignedManagerId
  plantId: string
  status: string
  createdBy: CreatedBy
  createdDate: string
  batchLots: BatchLot[]
  batchProducts: BatchProduct[]
  processingLoss: ProcessingLoss
}
export interface AssignedManagerId {
  userid: string
  username: string
}

export interface CreatedBy {
  userid: string
  username: string
}



export interface Employee {
  id: string;
  name: string;
}

export interface EmployeeData {
  empId: string;
  empName: string;
  posistionId: string;
  posistionName: string;
}

export interface EmployeeByRoleResponse {
  success: boolean;
  data: EmployeeData[];
  message: string;
}

export interface BatchRequestUpdate {
  batchkey: string
  batchCode: string
  assignedManagerId: string
  plantId: string
  unitId: number
  status: string
}

export interface PackSizeUpdate {
  packSizeKey: string
  packSize: string
  primaryBagsCount: number
  secondaryBagsCount: number
}

export interface PackSizeAdd {
  packSize: string
  primaryBagsCount: number
  secondaryBagsCount: number
}

export interface BatchLotUpdate {
  lotkey: string;
  cropId: number;
  lotNumber: string;
  lotWeight: number;
}
export interface AddBatchLot {
  batchkey: string
  cropId: number
  lotNumber: string
  lotWeight: number
}

export interface BatchproductsUpdate {
  productkey: string
  batchkey: string
  finl_lot_id: string
  productName: string
  expectedOutputWeight: number
  benchmarkLossPercentage: number
  assignedSupervisorId: string
  modifiedBy: string
}

export interface ChemicalsUpdate {
  chemicalkey: string
  chemicalName: string
  waterPercentage: number
  chemicaltype: string
}

export interface ChemicalsAdd {
  batchProdkey: string
  chemicalName: string
  waterPercentage: number
  chemicaltype: string
}

export interface AddChemicalRequest {
  cropId: number
  productCode: string
  chemicalName: string
  iType?: string
  iUnits?: string
  dosageQty?: string
}

export interface PackagingMaterialUpdate {
  packagingMaterialkey: string
  materialName: string
  materialType: string
  unit: string
  noOfrolls: number
  weight: number
}

export interface PackingMaterialAdd {
  batchProdkey: string,
  materialName: string,
  materialType: string,
  unit: string,
  weight: number,
  noOfrolls: number
}

export interface FieldConfig {
  name: string;
  label: string;
  type: string;
  required?: boolean;
}

export interface PopupConfig {
  heading: string;
  fields?: FieldConfig[];
  gridTemplate: string; // CSS grid template string
  initialValues?: Record<string, any>;
}

export interface DynamicPopupProps {
  config?: PopupConfig;
  onSave?: (data: Record<string, any>) => void;
  onClose?: () => void;
  isOpen?: boolean;
}

export interface LotDetailsUpdate {
  lotNumber: string;
  productionQuantity: string;
  crop: any;
  year: string
}

export interface fieldTypes {
  fieldType: string
  fieldName: string[]
}

export interface ChemicalDosages {
  cropId: number
  iType: string
  iUnits: string
  chemicalName: string
  dosageQty: string
  productCode: any
}

export interface PlantResponce {
  success: boolean
  data: string[]
  message: string
}

export interface FormErrorsBatchCreate {
  batchCode?: string;
  assignedManagerId?: string;
  plantId?: string;
  lots?: [{ cropId?: string; lotNumber?: string; lotWeight?: string; year?: string }];
  products?: { [key: number]: {
    productName?: string;
    expectedOutputWeight?: string;
    benchmarkLossPercentage?: string;
    finalProductLOTNumber?: string;
    packSets?: { [key: number]: { packSize?: string; primaryBagsCount?: string; secondaryBagsCount?: string } };
    packMaterials?: { [key: number]: { materialName?: string; unit?: string; noOfrolls?: string; weight?: string; materialType?: string } };
  }};
}

export interface BatchLot {
  lotkey: string;
  batchkey: string;
  cropId: number;
  lotNumber: string;
  lotWeight: number;
  finalLotNumber: string | null;
  createdAt: string;
  createdBy: string;
  modifiedAt: string | null;
  modifiedBy: string | null;
}

// API response interface
export interface BatchLotsResponse {
  success: boolean;
  data: BatchLot[];
  message: string;
}
